"use client";

import type { ShowReviewData } from "@workspace/types";
import { parseSpecialGuestsForBadges } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReviewLovedButton } from "@/components/features/festival/review/review-loved-button";
import { ReviewStars } from "@/components/features/festival/review/review-stars";
import { ReviewStatusButton } from "@/components/features/festival/review/review-status-button";
import { Facepile } from "@/components/shared/facepile";
import { GenreBadgeList } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { SpotifyPreviewPill } from "@/components/shared/spotify-preview-pill";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { useRealtimeShowReview } from "@/hooks/features/show/use-realtime-show-review";
import { isEmptyReview } from "@workspace/shared-utils";
import { useCurrentUser } from "@/stores";

interface FollowerInteraction {
	id: string;
	username: string | null;
	display_name: string;
	manual_avatar_url?: string | null;
	google_avatar_url?: string | null;
	rating?: number;
	loved?: boolean;
	must_see?: boolean;
	want_to_see?: boolean;
	saw_live?: boolean;
	saw_stream?: boolean;
	content?: string;
}

interface ShowReviewModalProps {
	isOpen: boolean;
	onClose: () => void;
	showId: string;
	showSlug: string;
	showTitle: string;
	artistName: string;
	artistSlug?: string; // Artist slug for URL generation
	artistId?: string | null; // Add artistId for real-time hook
	artistImageUrl?: string | null; // Artist photo URL
	artistSpotifyId?: string | null; // Artist Spotify ID for preview
	artistGenres?: string[] | null;
	specialGuests?: string | null; // Special guests string
	showDate: string;
	venue: string;
	location: string;
	imageUrl?: string | null;
	onSaved?: () => void; // Optional callback after save
	socialInteractions?: FollowerInteraction[];
}

export function ShowReviewModal({
	isOpen,
	onClose,
	showId,
	showSlug,
	artistName,
	artistSlug,
	artistId,
	artistImageUrl,
	artistSpotifyId,
	artistGenres,
	specialGuests,
	showDate,
	venue,
	location,
	onSaved,
	socialInteractions = [],
}: ShowReviewModalProps) {
	const currentUser = useCurrentUser();

	// Local state for form inputs
	const [localRating, setLocalRating] = useState(0);
	const [localContent, setLocalContent] = useState("");
	const [localLoved, setLocalLoved] = useState(false);
	const [localSawLive, setLocalSawLive] = useState(false);
	const [localSawStream, setLocalSawStream] = useState(false);
	const [localWantToSee, setLocalWantToSee] = useState(false);
	const [localMustSee, setLocalMustSee] = useState(false);

	// Use real-time hook for instant updates
	const { review, saveReview, isPending } = useRealtimeShowReview({
		showId,
		artistId: artistId || null,
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id && !!showId && isOpen,
	});

	// Track if we had a review when the modal opened (for button text logic)
	const hadInitialReviewRef = useRef(false);

	// Set the initial review state when modal opens
	useEffect(() => {
		if (isOpen) {
			hadInitialReviewRef.current = !!review;
		}
	}, [isOpen, review]);

	// Initialize form with existing review data
	useEffect(() => {
		if (isOpen && review) {
			setLocalRating(review.rating || 0);
			setLocalContent(review.content || "");
			setLocalLoved(review.loved || false);
			setLocalSawLive(review.sawLive || false);
			setLocalSawStream(review.sawStream || false);
			setLocalWantToSee(review.wantToSee || false);
			setLocalMustSee(review.mustSee || false);
		} else if (isOpen && !review) {
			// Reset to defaults if no review exists
			setLocalRating(0);
			setLocalContent("");
			setLocalLoved(false);
			setLocalSawLive(false);
			setLocalSawStream(false);
			setLocalWantToSee(false);
			setLocalMustSee(false);
		}
	}, [isOpen, review]);

	// Handle save - matches original design where save handles both update and remove
	const handleSave = useCallback(async () => {
		if (!currentUser) return;

		await saveReview({
			rating: localRating,
			content: localContent,
			loved: localLoved,
			sawLive: localSawLive,
			sawStream: localSawStream,
			wantToSee: localWantToSee,
			mustSee: localMustSee,
		});

		onClose();
		onSaved?.();
	}, [
		currentUser,
		localRating,
		localContent,
		localLoved,
		localSawLive,
		localSawStream,
		localWantToSee,
		localMustSee,
		saveReview,
		onClose,
		onSaved,
	]);

	// Handle status button changes with proper exclusivity
	const handleStatusChange = useCallback(
		(field: keyof ShowReviewData) => {
			if (field === "sawLive") {
				setLocalSawLive(!localSawLive);
				setLocalSawStream(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (field === "sawStream") {
				setLocalSawStream(!localSawStream);
				setLocalSawLive(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (field === "wantToSee") {
				setLocalWantToSee(!localWantToSee);
				setLocalMustSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			} else if (field === "mustSee") {
				setLocalMustSee(!localMustSee);
				setLocalWantToSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			}
		},
		[localSawLive, localSawStream, localWantToSee, localMustSee],
	);

	// Create a review data object from local state to check if it's empty
	const localReviewData = {
		rating: localRating,
		content: localContent,
		loved: localLoved,
		sawLive: localSawLive,
		sawStream: localSawStream,
		wantToSee: localWantToSee,
		mustSee: localMustSee,
	};

	const isLocalReviewEmpty = isEmptyReview(localReviewData);

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="w-full max-w-[95%] p-0 sm:max-w-md">
				<DialogTitle className="sr-only">Review Show</DialogTitle>
				<DialogDescription className="sr-only">
					Rate and review this show. Share your experience and add personal
					notes about the performance.
				</DialogDescription>
				<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
					{/* Artist photo and info - matching SetInfoDisplay pattern exactly */}
					<div className="flex items-start gap-3">
						{/* Artist Photo */}
						<ClickableArtistPhoto
							href={`/artists/${createArtistSlug(artistName, artistSlug)}`}
							name={artistName}
							imageUrl={artistImageUrl}
							size="lg"
							priority={false}
							rounded="card"
							aspectRatio="square"
						/>

						{/* Artist Info (name as title, show details as subtitle) */}
						<div className="min-w-0 flex-1">
							<h3 className="font-semibold text-base leading-tight line-clamp-2">
								{artistName}
							</h3>
							<div>
								<div className="text-muted-foreground text-xs">{venue}, {location}</div>
								<div className="text-muted-foreground text-xs">{showDate}</div>
							</div>
						</div>
					</div>

					{/* Genres, Preview, Special Guests, and Following */}
					{(() => {
						// Filter out current user from social interactions - only show people you follow
						const followingInteractions = socialInteractions.filter(
							(interaction) => interaction.id !== currentUser?.id
						);
						const hasGenres = artistGenres && artistGenres.length > 0;
						const hasPreview = !!artistSpotifyId;
						const parsedSpecialGuests = specialGuests ? parseSpecialGuestsForBadges(specialGuests) : [];
						const hasSpecialGuests = parsedSpecialGuests.length > 0;
						const hasFollowing = followingInteractions.length > 0;

						if (!hasGenres && !hasPreview && !hasSpecialGuests && !hasFollowing) return null;

						return (
							<div className="space-y-3">
								{/* Genres and Preview row */}
								{(hasGenres || hasPreview) && (
									<div className={hasGenres && hasPreview ? "grid grid-cols-2 gap-4" : ""}>
										{/* Genres section */}
										{hasGenres && (
											<div className="space-y-1">
												<Label>Genres</Label>
												<GenreBadgeList genres={artistGenres} className="gap-2" />
											</div>
										)}
										{/* Preview section */}
										{hasPreview && (
											<div className="space-y-1">
												<Label>Preview</Label>
												<SpotifyPreviewPill
													spotifyId={artistSpotifyId}
													artistName={artistName}
												/>
											</div>
										)}
									</div>
								)}

								{/* Following section */}
								{hasFollowing && (
									<div className="space-y-1">
										<Label>People you follow</Label>
										<div>
											<Facepile
												interactions={followingInteractions}
												size="sm"
												allowHoverInModal={true}
											/>
										</div>
									</div>
								)}

								{/* Special Guests section */}
								{hasSpecialGuests && (
									<div className="space-y-1">
										<Label>Special Guests</Label>
										<div className="flex flex-wrap gap-1">
											{parsedSpecialGuests.map((guest, index) => (
												<Link
													key={index}
													href={`/artists/${createArtistSlug(guest)}`}
													className="cursor-pointer rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors duration-200 hover:bg-primary/20"
												>
													{guest}
												</Link>
											))}
										</div>
									</div>
								)}
							</div>
						);
					})()}
				</DialogHeader>

				<div className="px-6">
					<div className="space-y-4">
						<div className="relative flex items-center justify-center">
							<div className="flex items-center">
								<ReviewStars
									rating={localRating}
									onChange={(newRating) => setLocalRating(newRating)}
								/>

								<div className="mx-2 h-10 w-px bg-border/50 md:mx-4 md:h-14" />

								<ReviewLovedButton
									loved={localLoved}
									onChange={(newLoved) => setLocalLoved(newLoved)}
								/>
							</div>
						</div>

						<div className="flex flex-wrap justify-center gap-1 text-sm sm:gap-2 md:gap-3">
							<ReviewStatusButton
								label="Want to See"
								icon="eye"
								isActive={localWantToSee}
								activeClassName="text-radix-plum-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("wantToSee")}
							/>
							<ReviewStatusButton
								label="Must See"
								icon="flag"
								isActive={localMustSee}
								activeClassName="text-radix-violet-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("mustSee")}
							/>
							<ReviewStatusButton
								label="Saw Live"
								icon="mic"
								isActive={localSawLive}
								activeClassName="text-radix-indigo-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawLive")}
							/>
							<ReviewStatusButton
								label="Saw Stream"
								icon="live"
								isActive={localSawStream}
								activeClassName="text-radix-ruby-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawStream")}
							/>
						</div>

						<Textarea
							placeholder="Write your review here (optional)..."
							className="min-h-[100px]"
							value={localContent}
							onChange={(e) => setLocalContent(e.target.value)}
						/>
					</div>
				</div>

				<DialogFooter className="px-6 pt-0 pb-6">
					<div className="flex w-full items-center justify-between">
						<Button variant="secondary" asChild>
							<Link href={`/shows/${showSlug}`}>View Details</Link>
						</Button>

						<div className="flex gap-2">
							<Button variant="ghost" onClick={onClose} type="button">
								Cancel
							</Button>
							<Button
								onClick={handleSave}
								disabled={isPending}
								variant={
									isLocalReviewEmpty && hadInitialReviewRef.current
										? "destructive"
										: "default"
								}
								type="button"
							>
								{isPending
									? "Saving..."
									: isLocalReviewEmpty && hadInitialReviewRef.current
										? "Remove Review"
										: hadInitialReviewRef.current
											? "Update"
											: "Save"}
							</Button>
						</div>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
